import { useEffect, useState } from 'react'
import { Button, Card, Table, Tag, Typography } from 'antd'
import { useNavigate } from 'react-router-dom'
import { getRefunds } from '../../services/refund'
import { formatTimestamp } from '../../utils/format'
import type { MerchantRefund, Pagination } from '../../types'

const { Text, Title } = Typography

const statusLabels: Record<string, string> = {
  CREATED: '已建立',
  SUBMITTING: '提交中',
  PROCESSING: '處理中',
  SUCCEEDED: '已成功',
  FAILED: '已失敗',
  UNKNOWN: '未知',
}

const statusColors: Record<string, string> = {
  CREATED: 'default',
  SUBMITTING: 'blue',
  PROCESSING: 'gold',
  SUCCEEDED: 'green',
  FAILED: 'red',
  UNKNOWN: 'default',
}

export default function Refunds() {
  const navigate = useNavigate()
  const [refunds, setRefunds] = useState<MerchantRefund[]>([])
  const [pagination, setPagination] = useState<Pagination>({ page: 1, size: 20, total: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)

  const fetchRefunds = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getRefunds({ page, size })
      .then((res) => {
        setRefunds(res.data.items)
        setPagination(res.data.pagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchRefunds()
  }, [])

  const columns = [
    {
      title: '退款號',
      dataIndex: 'id',
      key: 'id',
      width: 190,
    },
    {
      title: '訂單號',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 190,
      render: (orderId: string) => (
        <Button type="link" size="small" onClick={() => navigate(`/dashboard/orders/${orderId}`)}>
          {orderId}
        </Button>
      ),
    },
    {
      title: '金額',
      key: 'amount',
      render: (_: unknown, record: MerchantRefund) => `${record.amount} ${record.currency}`,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason: string) => reason || '-',
    },
    {
      title: '錯誤信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (message?: string | null) => message ? <Text type="danger">{message}</Text> : '-',
    },
    {
      title: '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (ts: number) => formatTimestamp(ts),
    },
    {
      title: '成功時間',
      dataIndex: 'succeededAt',
      key: 'succeededAt',
      render: (ts?: number | null) => formatTimestamp(ts),
    },
  ]

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>退款記錄</Title>
      <Card>
        <Table
          columns={columns}
          dataSource={refunds}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            onChange: (page, size) => {
              if (size !== pageSize) {
                setPageSize(size)
                fetchRefunds(1, size)
              } else {
                fetchRefunds(page, size)
              }
            },
          }}
        />
      </Card>
    </div>
  )
}
